import React from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";

/**
 * Drag-reorderable background tiles.
 *
 * The dnd-kit wiring mirrors `src/layout/SortableList` (PointerSensor with a
 * 5px activation distance so a plain click still selects the tile,
 * closestCenter, rectSortingStrategy) but stays local: SortableList is bound to
 * the settings-context template `order` and cannot carry a second list.
 *
 * `DndContext` and `SortableContext` render no DOM of their own, so the tiles
 * remain direct children of the `.bg-selector` grid and the layout is unchanged.
 */

export interface BgTileEntry {
  entryKey: string;
  position: number;
}

function SortableTile({id, children}: {id: string; children: React.ReactNode}) {
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id});
  return (
    <div
      ref={setNodeRef}
      className="bg-tile-sortable"
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 999 : "auto",
        position: "relative",
        opacity: isDragging ? 0.4 : 1,
        // Without this a touch-drag scrolls the sidebar instead of moving the tile.
        touchAction: "none",
      }}
      {...attributes}
      {...listeners}
    >
      {children}
    </div>
  );
}

export default function BgListTiles<Entry extends BgTileEntry>({
  entries,
  onReorder,
  renderTile,
}: {
  entries: Entry[];
  /** Called with the dragged entry's key and the key it was dropped onto. */
  onReorder: (fromKey: string, toKey: string) => void;
  renderTile: (entry: Entry) => React.ReactNode;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, {activationConstraint: {distance: 5}}),
    useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
  );

  const handleDragEnd = ({active, over}: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    onReorder(String(active.id), String(over.id));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext
        items={entries.map((entry) => entry.entryKey)}
        strategy={rectSortingStrategy}
      >
        {entries.map((entry) => (
          <SortableTile key={entry.entryKey} id={entry.entryKey}>
            {renderTile(entry)}
          </SortableTile>
        ))}
      </SortableContext>
    </DndContext>
  );
}
